import { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Map as MapIcon } from 'lucide-react';
import { Reveal } from '@/components/Reveal';
import { CONTACT_INFO } from '@/data/contactInfo';
import { useServices } from '@/data/useServices';
import { API_ROOT } from '@/data/runtimeApi';

const API = API_ROOT;

const TITLE_STYLE = {
  color: '#ED6B24',
  fontFamily: 'Gotham, sans-serif',
  fontWeight: 900,
  letterSpacing: '0.01em',
};

export default function Contacto() {
  const location = useLocation();
  const { services: SERVICES } = useServices();
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    comments: '',
    privacy: false,
    website: '', // honeypot (never visible to humans)
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  // Track when the form was mounted to reject bot-fast submissions
  const [mountTs] = useState(() => Date.now());
  // Optional Google reCAPTCHA v3 site key
  const recaptchaSiteKey = (process.env.REACT_APP_RECAPTCHA_SITE_KEY || '').trim();

  // Inject reCAPTCHA v3 script once when site key is present
  useEffect(() => {
    if (!recaptchaSiteKey) return;
    if (document.querySelector('script[data-recaptcha="v3"]')) return;
    const s = document.createElement('script');
    s.src = `https://www.google.com/recaptcha/api.js?render=${recaptchaSiteKey}`;
    s.async = true;
    s.defer = true;
    s.dataset.recaptcha = 'v3';
    document.head.appendChild(s);
  }, [recaptchaSiteKey]);

  // Scroll to hash anchor (e.g. #contactanos) when arriving via footer link
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      // Wait a tick so the layout is fully painted
      const t = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
      return () => clearTimeout(t);
    }
  }, [location.hash]);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'El nombre es requerido';
    if (!form.email.trim()) e.email = 'El correo es requerido';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Correo no válido';
    if (!form.phone.trim()) e.phone = 'El teléfono es requerido';
    else if (!/^[+\d][\d\s()+\-]{5,}$/.test(form.phone.trim())) e.phone = 'Teléfono no válido';
    if (!form.service) e.service = 'Selecciona un servicio de interés';
    if (!form.comments.trim()) e.comments = 'Cuéntanos brevemente tu necesidad';
    if (!form.privacy) e.privacy = 'Debes aceptar el Aviso de Privacidad';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const messageBody = [
        form.service ? `Servicio de interés: ${form.service}` : null,
        form.comments,
      ]
        .filter(Boolean)
        .join('\n\n');

      // Get reCAPTCHA token (if enabled)
      let recaptchaToken = null;
      if (recaptchaSiteKey && window.grecaptcha) {
        try {
          await new Promise((resolve) => window.grecaptcha.ready(resolve));
          recaptchaToken = await window.grecaptcha.execute(recaptchaSiteKey, {
            action: 'contact',
          });
        } catch (e) {
          // Continue without token; backend will reject if required
        }
      }

      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        message: messageBody,
        // antispam payload
        website: form.website, // honeypot (must be empty)
        elapsed_ms: Date.now() - mountTs,
        recaptcha_token: recaptchaToken,
      });
      toast.success('Mensaje enviado. Te contactaremos pronto.');
      setForm({
        name: '',
        phone: '',
        email: '',
        service: '',
        comments: '',
        privacy: false,
        website: '',
      });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'No pudimos enviar el mensaje. Intenta nuevamente o escríbenos al correo.';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const onChange = (k) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: value }));
    if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined }));
  };

  // Underline input style (transparent + only bottom border) — dark variant for light bg
  const inputClass =
    'w-full bg-transparent border-0 border-b border-[#3A4657]/40 px-1 pt-4 pb-2 text-[#58677A] placeholder-[#58677A]/55 outline-none focus:border-[#ED6B24] transition-colors duration-200 text-base';

  return (
    <div data-testid="contacto-page">
      {/* ========== NUESTRA OFICINA ========== */}
      <section
        className="relative pt-16 sm:pt-20 lg:pt-24 pb-20 lg:pb-24 text-[#58677A]"
        style={{
          backgroundColor: '#FFFFFF',
        }}
      >
        <div className="container-link lg:px-[10%]">
          <Reveal>
            <h1
              className="text-center uppercase mb-12 lg:mb-16"
              style={{ ...TITLE_STYLE, fontSize: 'clamp(22pt, 5.5vw, 32pt)' }}
              data-testid="contact-office-title"
            >
              NUESTRA OFICINA
            </h1>
          </Reveal>

          <div className="grid lg:grid-cols-[1fr_2.4fr] gap-10 lg:gap-16 items-start">
            {/* Left: map icon + locations */}
            <Reveal>
              <div className="flex flex-col items-start gap-5 lg:pl-6">
                <MapIcon
                  size={56}
                  strokeWidth={1.5}
                  className="text-[#58677A]"
                  data-testid="contact-map-icon"
                />
                <div className="text-base sm:text-lg leading-relaxed text-[#58677A]">
                  {CONTACT_INFO.locations.map((loc) => (
                    <p key={loc.city} data-testid={`contact-location-${loc.city}`}>
                      {loc.city}
                    </p>
                  ))}
                </div>
              </div>
            </Reveal>

            {/* Right: map */}
            <Reveal delay={0.1}>
              <div
                className="relative rounded-md overflow-hidden border border-[#3A4657]/15 shadow-sm aspect-[16/10] bg-white"
                data-testid="contact-map-frame"
              >
                <iframe
                  title="Ubicación Link Consultores - Cumbayá, Quito"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d6906.617576042086!2d-78.44100692120624!3d-0.21721335217738416!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x91d59175401e2e3b%3A0x75d6ffb371c1a80!2sLink%20Consultores%20de%20Mercadeo!5e0!3m2!1ses!2sar!4v1785516965280!5m2!1ses!2sar"
                  className="absolute inset-0 w-full h-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ========== CONTÁCTANOS (paper texture background, full width top→bottom) ========== */}
      <section
        id="contactanos"
        className="relative text-[#58677A] scroll-mt-24"
        data-testid="contact-form-section"
        style={{
          backgroundColor: '#EFECE7',
          backgroundImage: "url('/assets/backgrounds/contacto-bg.jpg')",
          backgroundRepeat: 'no-repeat',
          backgroundSize: '100% 100%',
          backgroundPosition: 'top center',
        }}
      >
        <div className="container-link lg:px-[10%] pt-20 sm:pt-24 lg:pt-28 pb-20 lg:pb-28 relative">
          <Reveal>
            <h2
              className="text-center uppercase mb-14 lg:mb-20"
              style={{ ...TITLE_STYLE, fontSize: 'clamp(22pt, 5.5vw, 32pt)' }}
              data-testid="contact-form-title"
            >
              CONTÁCTANOS
            </h2>
          </Reveal>

          <div className="grid lg:grid-cols-[1fr_1.3fr] gap-12 lg:gap-20 items-start">
            {/* LEFT — Tagline + email + WhatsApp */}
            <Reveal>
              <div className="text-[#58677A]">
                <h3
                  className="font-black uppercase leading-[1.1] mb-7 text-2xl sm:text-3xl lg:text-4xl"
                  data-testid="contact-tagline"
                >
                  APORTAMOS UNA MIRADA
                  <br />
                  EXTERNA A TU MARCA
                </h3>
                <p className="text-[#58677A]/80 text-sm sm:text-base mb-1">
                  Si lo prefieres, puedes enviarnos un correo a
                </p>
                <a
                  href={`mailto:${CONTACT_INFO.email}`}
                  className="text-[#ED6B24] hover:text-[#ff8242] font-semibold break-all text-base sm:text-lg transition-colors"
                  data-testid="contact-email-link"
                >
                  {CONTACT_INFO.email}
                </a>

                <div className="mt-8">
                  <a
                    href={`https://wa.me/${CONTACT_INFO.whatsapp}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 bg-[#ED6B24] hover:bg-[#ff8242] text-white font-semibold rounded-full pl-2 pr-6 py-2 transition-colors duration-200"
                    data-testid="contact-whatsapp-button"
                  >
                    <span className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
                      <img
                        src="/assets/icons/whatsapp.png"
                        alt=""
                        className="w-5 h-5 object-contain"
                        style={{ filter: 'brightness(0) invert(1)' }}
                      />
                    </span>
                    <span className="text-sm sm:text-base">Contáctanos por WhatsApp</span>
                  </a>
                </div>
              </div>
            </Reveal>

            {/* RIGHT — Form */}
            <Reveal delay={0.1}>
              <form onSubmit={onSubmit} noValidate data-testid="contact-form">
                {/* Honeypot — invisible to humans, attractive to bots */}
                <div aria-hidden="true" className="absolute opacity-0 pointer-events-none -left-[9999px] w-px h-px overflow-hidden">
                  <label>
                    Si eres humano, deja este campo en blanco
                    <input
                      type="text"
                      name="website"
                      tabIndex={-1}
                      autoComplete="off"
                      value={form.website}
                      onChange={onChange('website')}
                    />
                  </label>
                </div>

                <p className="text-[#58677A] text-base sm:text-lg leading-snug mb-7">
                  ¿Te ayudamos a potenciarla?
                  <br />
                  Escríbenos para más información
                </p>

                <div className="grid sm:grid-cols-2 gap-5 sm:gap-6">
                  <div>
                    <input
                      type="text"
                      value={form.name}
                      onChange={onChange('name')}
                      className={inputClass}
                      placeholder="Nombre Completo *"
                      data-testid="contact-name-input"
                      aria-label="Nombre Completo"
                    />
                    {errors.name && <p className="text-red-600 text-xs mt-1">{errors.name}</p>}
                  </div>
                  <div>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={onChange('phone')}
                      className={inputClass}
                      placeholder="Teléfono *"
                      data-testid="contact-phone-input"
                      aria-label="Teléfono"
                    />
                    {errors.phone && <p className="text-red-600 text-xs mt-1">{errors.phone}</p>}
                  </div>
                </div>

                <div className="mt-5 sm:mt-6">
                  <input
                    type="email"
                    value={form.email}
                    onChange={onChange('email')}
                    className={inputClass}
                    placeholder="Correo Electrónico *"
                    data-testid="contact-email-input"
                    aria-label="Correo Electrónico"
                  />
                  {errors.email && <p className="text-red-600 text-xs mt-1">{errors.email}</p>}
                </div>

                <div className="mt-5 sm:mt-6 relative">
                  <select
                    value={form.service}
                    onChange={onChange('service')}
                    className={`${inputClass} appearance-none cursor-pointer pr-8`}
                    data-testid="contact-service-select"
                    aria-label="Servicio de interés"
                  >
                    <option value="" className="bg-white text-[#58677A]">
                      Servicio de interés *
                    </option>
                    {SERVICES.map((s) => (
                      <option key={s.slug} value={s.name} className="bg-white text-[#58677A]">
                        {s.name}
                      </option>
                    ))}
                  </select>
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[#58677A]/60"
                  >
                    ▾
                  </span>
                  {errors.service && <p className="text-red-600 text-xs mt-1">{errors.service}</p>}
                </div>

                <div className="mt-5 sm:mt-6">
                  <textarea
                    value={form.comments}
                    onChange={onChange('comments')}
                    rows={3}
                    className={`${inputClass} resize-none`}
                    placeholder="Comentarios que ayuden a la reunión *"
                    data-testid="contact-comments-textarea"
                    aria-label="Comentarios"
                  />
                  {errors.comments && (
                    <p className="text-red-600 text-xs mt-1">{errors.comments}</p>
                  )}
                </div>

                <label
                  className="mt-8 flex items-start gap-3 cursor-pointer select-none"
                  data-testid="contact-privacy-label"
                >
                  <input
                    type="checkbox"
                    checked={form.privacy}
                    onChange={onChange('privacy')}
                    className="mt-1 w-4 h-4 accent-[#ED6B24] cursor-pointer flex-shrink-0"
                    data-testid="contact-privacy-checkbox"
                  />
                  <span className="text-[#58677A]/85 text-sm">
                    He leído y acepto el{' '}
                    <Link
                      to="/aviso-privacidad"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#ED6B24] hover:text-[#ff8242] underline underline-offset-2 font-semibold"
                      onClick={(e) => e.stopPropagation()}
                      data-testid="open-privacy-btn"
                    >
                      Aviso de Privacidad
                    </Link>
                  </span>
                </label>
                {errors.privacy && (
                  <p className="text-red-600 text-xs mt-1 ml-7">{errors.privacy}</p>
                )}

                <div className="mt-7 flex justify-end">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="bg-[#ED6B24] hover:bg-[#ff8242] disabled:opacity-60 text-white font-semibold rounded-full px-9 py-3 transition-colors duration-200"
                    data-testid="contact-submit-button"
                  >
                    {submitting ? 'Enviando…' : 'Enviar y validar'}
                  </button>
                </div>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
